import { useState } from 'react';
import {
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Download,
  Inbox,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from './button';

export interface DataTableProps<TData> {
  columns: ColumnDef<TData, unknown>[];
  data: TData[];
  loading?: boolean;
  /** Active la recherche globale interne (sinon filtrer en amont). */
  enableGlobalFilter?: boolean;
  globalFilter?: string;
  onGlobalFilterChange?: (value: string) => void;
  enableRowSelection?: boolean;
  onRowSelectionChange?: (selected: TData[]) => void;
  /** Export CSV (nom de fichier sans extension). */
  exportFileName?: string;
  pageSize?: number;
  /** Callback de clic sur une ligne (hors interactions internes). */
  onRowClick?: (row: TData) => void;
  emptyMessage?: string;
  className?: string;
}

/** Échappe et sérialise des lignes en CSV (compatible Excel, séparateur « ; »). */
function toCSV<TData>(columns: ColumnDef<TData, unknown>[], rows: TData[]): string {
  const cols = columns.filter((c) => 'accessorKey' in c) as Array<
    ColumnDef<TData, unknown> & { accessorKey: string; header?: unknown }
  >;
  const escape = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const header = cols.map((c) => escape(typeof c.header === 'string' ? c.header : c.accessorKey)).join(';');
  const body = rows
    .map((row) => cols.map((c) => escape((row as Record<string, unknown>)[c.accessorKey])).join(';'))
    .join('\n');
  // Pr\u00E9fixe BOM UTF-8 pour qu'Excel d\u00E9tecte correctement l'encodage.
  return `\uFEFF${header}\n${body}`;
}

/**
 * DataTable (TanStack Table v8) : tri, filtrage global, pagination, sélection
 * de lignes, export CSV/Excel, colonnes redimensionnables, squelette de chargement.
 * Brief — composant #2.
 */
export function DataTable<TData>({
  columns,
  data,
  loading = false,
  enableGlobalFilter = false,
  globalFilter,
  onGlobalFilterChange,
  enableRowSelection = false,
  onRowSelectionChange,
  exportFileName,
  pageSize = 10,
  onRowClick,
  emptyMessage = 'Aucune donnée à afficher.',
  className,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection, ...(globalFilter !== undefined ? { globalFilter } : {}) },
    enableRowSelection,
    columnResizeMode: 'onChange',
    onSortingChange: setSorting,
    onGlobalFilterChange,
    onRowSelectionChange: (updater) => {
      const next = typeof updater === 'function' ? updater(rowSelection) : updater;
      setRowSelection(next);
      if (onRowSelectionChange) {
        const selectedRows = table.getRowModel().rows.filter((r) => next[r.id]).map((r) => r.original);
        onRowSelectionChange(selectedRows);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: enableGlobalFilter ? getFilteredRowModel() : undefined,
    initialState: { pagination: { pageSize } },
  });

  const handleExport = () => {
    const csv = toCSV(columns, data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFileName ?? 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const colCount = columns.length;
  const rows = table.getRowModel().rows;

  return (
    <div className={cn('overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm', className)}>
      {exportFileName && (
        <div className="flex items-center justify-end border-b border-border px-4 py-2">
          <Button variant="secondary" size="sm" onClick={handleExport}>
            <Download /> Exporter CSV
          </Button>
        </div>
      )}

      {/* Vue MOBILE & TABLETTE (< lg) : une carte par ligne (couples libellé/valeur), sans scroll horizontal subi. */}
      <div className="divide-y divide-border lg:hidden">
        {loading ? (
          Array.from({ length: Math.min(pageSize, 5) }).map((_, i) => (
            <div key={`mc-skeleton-${i}`} className="p-4"><div className="skeleton h-14 w-full" /></div>
          ))
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-16 text-content-muted">
            <Inbox className="size-8" />
            <p className="text-sm">{emptyMessage}</p>
          </div>
        ) : (
          rows.map((row) => (
            <div
              key={row.id}
              onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              className={cn('flex flex-col gap-1.5 p-4', onRowClick && 'cursor-pointer active:bg-surface-hover')}
            >
              {row.getVisibleCells().map((cell) => {
                const h = cell.column.columnDef.header;
                const label = typeof h === 'string' ? h : '';
                return (
                  <div key={cell.id} className="flex items-start justify-between gap-3 text-sm">
                    {label && <span className="shrink-0 text-xs font-medium uppercase tracking-wide text-content-muted">{label}</span>}
                    <span className={cn('min-w-0', label ? 'text-right text-content-primary' : 'w-full')}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </span>
                  </div>
                );
              })}
            </div>
          ))
        )}
      </div>

      {/* Vue DESKTOP (lg+) : tableau classique. */}
      <div className="scrollbar-thin hidden overflow-x-auto lg:block">
        {/* w-full + minWidth : le tableau remplit toujours la carte (pas de bande
            blanche à droite) et ne défile que s'il est plus large que la carte. */}
        <table className="w-full border-collapse text-sm" style={{ minWidth: table.getTotalSize() }}>
          <thead className="bg-surface-primary">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id} className="border-b border-border">
                {hg.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      className="relative px-4 py-3 text-left align-middle"
                      style={{ width: header.getSize() }}
                    >
                      {header.isPlaceholder ? null : (
                        <button
                          type="button"
                          disabled={!canSort}
                          onClick={header.column.getToggleSortingHandler()}
                          className={cn(
                            'flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-content-muted',
                            canSort && 'cursor-pointer hover:text-content-secondary',
                          )}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            (sorted === 'asc' ? (
                              <ArrowUp className="size-3.5" />
                            ) : sorted === 'desc' ? (
                              <ArrowDown className="size-3.5" />
                            ) : (
                              <ChevronsUpDown className="size-3.5 opacity-50" />
                            ))}
                        </button>
                      )}
                      {header.column.getCanResize() && (
                        <span
                          onMouseDown={header.getResizeHandler()}
                          onTouchStart={header.getResizeHandler()}
                          className="absolute right-0 top-0 h-full w-1 cursor-col-resize select-none touch-none hover:bg-brand-primary"
                        />
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>

          <tbody>
            {loading ? (
              Array.from({ length: Math.min(pageSize, 6) }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b border-border">
                  {Array.from({ length: colCount }).map((__, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="skeleton h-4 w-full" />
                    </td>
                  ))}
                </tr>
              ))
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-content-muted">
                    <Inbox className="size-8" />
                    <p className="text-sm">{emptyMessage}</p>
                  </div>
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn(
                    'border-b border-border transition-colors last:border-0 hover:bg-surface-hover',
                    onRowClick && 'cursor-pointer',
                    row.getIsSelected() && 'bg-brand-primary/5',
                  )}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id} className="px-4 py-3 text-content-primary" style={{ width: cell.column.getSize() }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && table.getRowModel().rows.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border px-4 py-3">
          <p className="text-xs text-content-muted">
            {enableRowSelection && (
              <span className="mr-2 font-medium text-content-secondary">
                {table.getSelectedRowModel().rows.length} sélectionnée(s) ·
              </span>
            )}
            {table.getFilteredRowModel().rows.length} ligne(s)
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-content-muted">
              Page {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </span>
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              aria-label="Page précédente"
            >
              <ChevronLeft />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="h-8 w-8"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              aria-label="Page suivante"
            >
              <ChevronRight />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
